<template>
    <teleport to="body">
        <div
            v-show="open"
            ref="root"
            class="app-dialog-root fixed inset-0 overflow-y-auto"
            :style="{zIndex: dialogZIndex}"
        >
            <transition
                duration="300"
                enter-active-class="ease-out duration-300"
                enter-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="ease-in duration-200"
                leave-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div
                    v-show="open"
                    class="app-dialog-overlay fixed inset-0 transition-opacity"
                    @click="requestClose('backdrop')"
                >
                    <div class="app-dialog-backdrop absolute inset-0"></div>
                </div>
            </transition>

            <div class="relative z-[1] flex min-h-full items-center justify-center p-4 sm:p-6">
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-class="translate-y-4 opacity-0 sm:translate-y-0 sm:scale-95"
                    enter-to-class="translate-y-0 opacity-100 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="translate-y-0 opacity-100 sm:scale-100"
                    leave-to-class="translate-y-4 opacity-0 sm:translate-y-0 sm:scale-95"
                >
                    <section
                        v-show="open"
                        ref="panel"
                        :aria-labelledby="hasTitle ? resolvedTitleId : null"
                        :aria-label="!hasTitle ? ariaLabel : null"
                        aria-modal="true"
                        class="app-dialog-panel relative flex w-full max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-md bg-white text-left shadow-xl transition-all sm:max-h-[calc(100dvh-3rem)]"
                        :class="[resolvedSizeClass, panelClass]"
                        role="dialog"
                        tabindex="-1"
                        @click.stop
                    >
                        <button
                            v-if="showCloseButton"
                            type="button"
                            class="absolute right-6 top-6 z-10 text-gray-400 hover:text-gray-600 focus:outline-none"
                            :disabled="busy"
                            :aria-label="closeLabel"
                            @click="requestClose('button')"
                        >
                            <svg-vue class="h-4 w-4" icon="font-awesome.times-light"></svg-vue>
                        </button>

                        <header v-if="hasTitle" class="flex-shrink-0" :class="resolvedHeaderClass">
                            <div :id="resolvedTitleId" :class="resolvedTitleClass">
                                <slot name="title">{{ title }}</slot>
                            </div>
                        </header>

                        <div class="min-h-0 flex-1 overflow-y-auto" :class="resolvedBodyClass">
                            <slot></slot>
                        </div>

                        <footer v-if="$slots.footer" :class="resolvedFooterClass">
                            <slot name="footer"></slot>
                        </footer>
                    </section>
                </transition>
            </div>
        </div>
    </teleport>
</template>

<script>
import {
    createDialogInstanceId,
    isTopDialog,
    registerDialog,
    unregisterDialog,
} from '@/utils/dialog-stack';

const SIZE_CLASSES = {
    sm: 'max-w-lg',
    md: 'max-w-2xl',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
};

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

export default {
    name: 'app-dialog',
    props: {
        open: {type: Boolean, default: false},
        title: {type: String, default: ''},
        titleId: {type: String, default: ''},
        ariaLabel: {type: String, default: 'Dialog'},
        closeLabel: {type: String, default: 'Dialog schließen'},
        size: {
            type: String,
            default: 'md',
            validator: (value) => Object.prototype.hasOwnProperty.call(SIZE_CLASSES, value),
        },
        headerVariant: {
            type: String,
            default: 'plain',
            validator: (value) => ['plain', 'sectioned'].includes(value),
        },
        showCloseButton: {type: Boolean, default: true},
        closeOnBackdrop: {type: Boolean, default: true},
        closeOnEscape: {type: Boolean, default: true},
        busy: {type: Boolean, default: false},
        panelClass: {type: [String, Array, Object], default: ''},
        bodyClass: {type: [String, Array, Object], default: ''},
        footerClass: {type: [String, Array, Object], default: ''},
    },
    emits: ['close', 'opened'],
    data() {
        return {
            dialogInstanceId: createDialogInstanceId(),
            dialogZIndex: 9999,
            previouslyFocusedElement: null,
            registered: false,
        };
    },
    computed: {
        hasTitle() {
            return Boolean(this.title || this.$slots.title);
        },
        resolvedTitleId() {
            return this.titleId || `${this.dialogInstanceId}-title`;
        },
        resolvedSizeClass() {
            return SIZE_CLASSES[this.size] || SIZE_CLASSES.md;
        },
        resolvedHeaderClass() {
            if (this.headerVariant === 'sectioned') {
                return 'stage-header-surface flex min-h-[64px] items-center border-b border-ultralight px-6';
            }

            return 'px-8 pt-8 sm:px-10 sm:pt-10';
        },
        resolvedTitleClass() {
            if (this.headerVariant === 'sectioned') {
                return 'pr-12 text-xl font-bold leading-7 text-gray-900';
            }

            return 'pr-12 text-xl font-bold leading-7 text-gray-900 sm:text-2xl sm:leading-8';
        },
        resolvedBodyClass() {
            if (this.bodyClass) {
                return this.bodyClass;
            }

            if (this.headerVariant === 'sectioned') {
                return 'p-6';
            }

            return this.hasTitle
                ? 'px-8 pb-8 pt-6 sm:px-10 sm:pb-10'
                : 'p-8 sm:p-10';
        },
        resolvedFooterClass() {
            return [
                'stage-footer-surface flex min-h-[56px] flex-shrink-0 items-stretch justify-end border-t border-ultralight p-0',
                this.footerClass,
            ];
        },
    },
    watch: {
        open: {
            immediate: true,
            handler(value) {
                if (value) {
                    this.openDialog();
                } else {
                    this.closeDialog();
                }
            },
        },
    },
    beforeUnmount() {
        this.closeDialog(false);
    },
    methods: {
        openDialog() {
            if (this.registered || typeof document === 'undefined') return;

            this.previouslyFocusedElement = document.activeElement;
            const stackIndex = registerDialog(this.dialogInstanceId);
            this.dialogZIndex = 9999 + (stackIndex * 20);
            this.registered = true;
            document.addEventListener('keydown', this.handleKeydown, true);

            this.$nextTick(() => {
                this.focusInitialElement();
                this.$emit('opened');
            });
        },
        closeDialog(restoreFocus = true) {
            if (!this.registered) return;

            document.removeEventListener('keydown', this.handleKeydown, true);
            unregisterDialog(this.dialogInstanceId);
            this.registered = false;

            const focusTarget = this.previouslyFocusedElement;
            if (restoreFocus && focusTarget && typeof focusTarget.focus === 'function') {
                this.$nextTick(() => {
                    if (focusTarget.isConnected) focusTarget.focus();
                });
            }
            this.previouslyFocusedElement = null;
        },
        requestClose(source) {
            if (this.busy) return;
            if (source === 'backdrop' && !this.closeOnBackdrop) return;
            if (source === 'escape' && !this.closeOnEscape) return;
            if (!isTopDialog(this.dialogInstanceId)) return;

            this.$emit('close');
        },
        handleKeydown(event) {
            if (!this.open || !isTopDialog(this.dialogInstanceId)) return;

            if (event.key === 'Escape') {
                event.preventDefault();
                this.requestClose('escape');
                return;
            }

            if (event.key === 'Tab') {
                this.keepFocusInsideDialog(event);
            }
        },
        focusInitialElement() {
            const panel = this.$refs.panel;
            if (!panel) return;

            const preferredSelectors = [
                '[data-dialog-autofocus]',
                '[autofocus]',
                'input:not([disabled]):not([type="hidden"])',
                'select:not([disabled])',
                'textarea:not([disabled])',
                'button:not([disabled])',
                'a[href]',
            ];
            const target = preferredSelectors
                .map((selector) => panel.querySelector(selector))
                .find((element) => element && element.getClientRects().length > 0);

            (target || panel).focus();
        },
        keepFocusInsideDialog(event) {
            const panel = this.$refs.panel;
            if (!panel) return;

            const elements = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR))
                .filter((element) => element.getClientRects().length > 0);
            if (!elements.length) {
                event.preventDefault();
                panel.focus();
                return;
            }

            const first = elements[0];
            const last = elements[elements.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        },
    },
};
</script>
